/**
 * uxTracker.js — Émetteur d'événements UX vers NOTRE backend (mesure d'usage interne).
 *
 * Confidentialité :
 *   • Aucun cookie, aucun service tiers : les événements partent uniquement vers notre API.
 *   • Le choix « Analytiques » du bandeau de préférences est respecté : si l'utilisateur a
 *     refusé (cookie_consent.analytics === false), plus aucun événement n'est mis en file
 *     ni envoyé. Le choix est relu à chaque événement, donc un refus prend effet immédiatement.
 *
 * Usage minimal dans App.jsx :
 *   import { initUXTracker, trackPageView } from "./uxTracker";
 *   useEffect(() => { initUXTracker(); }, []);
 *   useEffect(() => { trackPageView("MarketList"); }, [currentRoute]);
 *
 * Pour tracker un clic depuis n'importe quel composant :
 *   import { trackClick } from "./uxTracker";
 *   <button onClick={() => { trackClick("trade_confirm_buy"); handleBuy(); }}>
 *
 * Pour tracker une erreur affichée :
 *   trackError("insufficient_balance", { page: "TradeModal" });
 *
 * Pour tracker la latence d'un trade :
 *   const t0 = performance.now();
 *   await placeTrade();
 *   trackTradeExec(performance.now() - t0);
 */

import axios from "axios";

const API_BASE  = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
const TRACK_URL = `${API_BASE}/api/v1/admin/ux/track/`;

/* ─── Consentement ──────────────────────────────────────────────────────── */
// Écrit par le bandeau de préférences (Footer.jsx) : { analytics, marketing, functional }.
// Pas encore de choix → mesure interne anonyme autorisée ; refus explicite → rien n'est envoyé.
function isTrackingAllowed() {
  try {
    const raw = localStorage.getItem("cookie_consent");
    if (!raw) return true;
    return JSON.parse(raw).analytics !== false;
  } catch {
    return true;
  }
}

/* ─── Session & device ──────────────────────────────────────────────────── */
const SESSION_KEY = "evenmt_ux_session";

function getSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function getDevice() {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|Nexus 7/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

/* ─── Core send ─────────────────────────────────────────────────────────── */

// In-memory queue. Failed sends are dropped silently — UX tracking must
// never block or break the app.
let _queue = [];
let _flushTimer = null;

function _enqueue(payload) {
  if (!isTrackingAllowed()) {          // refus → on vide tout et on n'enregistre rien
    _queue = [];
    if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null; }
    return;
  }
  _queue.push(payload);
  if (_queue.length >= 10) {
    _flush();
  } else if (!_flushTimer) {
    _flushTimer = setTimeout(_flush, 2500);
  }
}

function _flush() {
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null; }
  if (!_queue.length) return;
  if (!isTrackingAllowed()) { _queue = []; return; }
  const batch = _queue.splice(0, _queue.length);
  const token = localStorage.getItem("access_token");

  batch.forEach(payload => {
    axios.post(TRACK_URL, payload, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      timeout: 5000,
    }).catch(() => {/* fail silently */});
  });
}

// Flush on page unload to not lose the last events
function _attachUnload() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeunload", _flush);
  // visibilitychange catches mobile-app-switch cases
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _flush();
  });
}

/* ─── Public API ────────────────────────────────────────────────────────── */

let _initialized = false;

export function initUXTracker() {
  if (_initialized) return;
  _initialized = true;
  _attachUnload();
  // Send initial session event
  _enqueue({
    event_type: "session",
    session_id: getSessionId(),
    device:     getDevice(),
    details:    { referrer: typeof document !== "undefined" ? document.referrer : "" },
  });
}

export function trackPageView(page) {
  if (!page) return;
  _enqueue({
    event_type: "page_view",
    session_id: getSessionId(),
    page:       String(page).slice(0, 80),
    device:     getDevice(),
  });
}

export function trackClick(element, details = {}) {
  if (!element) return;
  _enqueue({
    event_type: "click",
    session_id: getSessionId(),
    element:    String(element).slice(0, 120),
    page:       details.page || "",
    device:     getDevice(),
    details,
  });
}

export function trackError(errorCode, details = {}) {
  if (!errorCode) return;
  _enqueue({
    event_type: "error",
    session_id: getSessionId(),
    error_code: String(errorCode).slice(0, 80),
    page:       details.page || "",
    device:     getDevice(),
    details,
  });
}

export function trackTradeExec(latencyMs, details = {}) {
  _enqueue({
    event_type: "trade_exec",
    session_id: getSessionId(),
    latency_ms: Math.round(latencyMs),
    device:     getDevice(),
    details,
  });
}

// Manual flush (e.g. on logout)
export function flushTracker() { _flush(); }